import { useCallback } from 'react';
import { RichEditor } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import { uploadProductImage } from '../../../services/productImages';
import { ProductImageField } from './ProductImageField';

interface MerchandisingSectionProps {
    imageFile: File | null;
    storedImageFileId: string | null;
    onPickImage: (file: File | null) => void;
    onRemoveStoredImage: () => void;
    onRejectImage: (reason: string) => void;
    copyDoc: TiptapDoc;
    onCopyDocChange: (doc: TiptapDoc) => void;
    sellableInWebstore: boolean;
    onSellableChange: (sellable: boolean) => void;
    /** What the product still lacks before it can be sold; empty = ready. */
    readinessGaps: string[];
    disabled?: boolean;
}

/** Webstore-facing product presentation: hero image + rich marketing copy. */
export function MerchandisingSection({
    imageFile,
    storedImageFileId,
    onPickImage,
    onRemoveStoredImage,
    onRejectImage,
    copyDoc,
    onCopyDocChange,
    sellableInWebstore,
    onSellableChange,
    readinessGaps,
    disabled
}: MerchandisingSectionProps) {
    const handleImageUpload: RichEditorImageUploadHandler = useCallback(async (file) => {
        const { url, fileId } = await uploadProductImage(file);
        return { url, fileId };
    }, []);

    // Ticking the box makes the product purchasable by card, so the toggle
    // stays out of reach until the storefront has something to render.
    const blocked = readinessGaps.length > 0 && !sellableInWebstore;

    return (
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4">
            <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-semibold text-gray-700">Webstore</span>
                <span className="text-[11px] text-gray-400">
                    Shown to practices in the webstore. Description is the internal charge line-item
                    text.
                </span>
            </div>

            <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-gray-700">Product image</span>
                <ProductImageField
                    file={imageFile}
                    storedFileId={storedImageFileId}
                    disabled={disabled}
                    onPick={onPickImage}
                    onRemoveStored={onRemoveStoredImage}
                    onReject={onRejectImage}
                />
            </label>

            <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-gray-700">Marketing copy</span>
                <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-[220px] focus-within:border-gray-400 transition-colors">
                    <RichEditor
                        value={copyDoc}
                        onChange={onCopyDocChange}
                        onImageUpload={handleImageUpload}
                        placeholder="What a practice reads before adding this to their cart"
                        chrome="flush"
                        autoFocus={false}
                        readOnly={disabled}
                    />
                </div>
            </div>
            <label
                className={`flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 ${blocked ? 'bg-gray-50' : ''}`}
            >
                <input
                    type="checkbox"
                    checked={sellableInWebstore}
                    disabled={disabled || blocked}
                    onChange={(e) => onSellableChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-300 disabled:opacity-40"
                />
                <span className="flex flex-col gap-0.5">
                    <span
                        className={`text-[13px] font-semibold ${blocked ? 'text-gray-400' : 'text-gray-700'}`}
                    >
                        Sellable in webstore
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {blocked
                            ? `Needs ${readinessGaps.join(', ')} first.`
                            : 'Practices can add this product to their cart and pay by card.'}
                    </span>
                </span>
            </label>
        </div>
    );
}
